import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { resolveRefDisplay } from './refLabelMeta';

type RefLabelProps = {
    /** The resolved name. May be blank when the server could not resolve the id. */
    name: string | null | undefined;
    /** The referenced id — shown as a fragment when the name is missing. */
    id?: string | null;
    /** The referenced row has been deleted. */
    deleted?: boolean;
    /** The referenced row is archived — live, but out of the pickers. */
    archived?: boolean;
    /** Extra classes for the name text, so callers keep their typography. */
    className?: string;
    /** Literal, static testid prefix from the owning widget. */
    slot?: string;
    /**
     * Rendered instead of "Unknown" when the reference is legitimately absent —
     * no name and no id — e.g. "No branch" on a purchase that predates branch
     * linking. Without it a blank reads as a dangling reference (BOS-161).
     */
    absentLabel?: string;
};

/**
 * Renders a reference to another record — a brand, a category, a supplier, a
 * branch — together with what state that record is in.
 *
 * <p><b>Never renders an empty string.</b> A blank cell reads as "this has no
 * name", which is a different and much less useful claim than "this used to be
 * called Borjomi and has been deleted" — and a blank is exactly what the Sales
 * page showed for months (bos-api BOS-122). A deleted row is muted with a
 * <c>(deleted)</c> suffix; an archived one keeps its name and gains a chip; an
 * id that resolved to nothing at all becomes <c>Unknown (a1b2c3d4…)</c>, so the
 * row stays traceable instead of merely empty.</p>
 */
export const RefLabel: React.FC<RefLabelProps> = ({
    name,
    id,
    deleted = false,
    archived = false,
    className,
    slot,
    absentLabel,
}) => {
    const { t } = useTranslation('components');

    const display = resolveRefDisplay({
        name,
        id,
        deleted,
        archived,
        deletedMarker: t('deletedMarker'),
        unknownLabel: t('refLabel.unknown'),
        absentLabel,
    });

    // An orphan is not a state the product supports — a deleted row is. Warn in
    // development so it surfaces while someone can still find the cause,
    // without ever reaching a user.
    React.useEffect(() => {
        if (display.isOrphan && process.env.NODE_ENV !== 'production') {
            console.warn(
                `[RefLabel] Unresolvable reference${id ? ` "${id}"` : ''} — ` +
                    'the server returned no name for it. This is a dangling ' +
                    'reference, not a deleted row; run the reference audit.',
            );
        }
    }, [display.isOrphan, id]);

    return (
        <span
            className="inline-flex items-center gap-1.5"
            {...(slot ? getNodeTestIdProp(slot) : {})}
        >
            <span
                className={`${display.isDeleted ? 'text-muted line-through decoration-1' : ''} ${className ?? ''}`.trim()}
                data-ref-state={
                    display.isOrphan
                        ? 'orphan'
                        : display.isDeleted
                          ? 'deleted'
                          : display.isArchived
                            ? 'archived'
                            : 'live'
                }
            >
                {display.text}
            </span>
            {display.isArchived ? (
                <Chip color="default" size="sm">
                    {t('refLabel.archived')}
                </Chip>
            ) : null}
        </span>
    );
};
